import React, { useEffect, useState, useCallback } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { VenueProvider } from "./context/VenueContext";
import LandingScreen from "./screens/LandingScreen";
import AuthScreen from "./screens/AuthScreen";
import OnboardingScreen from "./screens/OnboardingScreen";
import MainApp from "./screens/MainApp";
import {
  exchangeCodeForToken,
  getSavedSpotifyToken,
  refreshSpotifyToken,
  getSpotifyUserProfile,
  clearSpotifyToken,
} from "./services/spotifyService";
import { getUserProfile, signInWithSpotifyProfile } from "./services/authService";
import { auth } from "./services/firebase";
import { syncSpotifyAccount, updateUserProfile } from "./services/userService";

function needsOnboarding(profile) {
  if (!profile) return false;
  if (profile.onboardingComplete === true) return false;
  return !profile.displayName?.trim() || profile.onboardingComplete === false;
}

function AppRouter() {
  const { user, loading, refreshProfile } = useAuth();
  const [spotifyToken, setSpotifyToken] = useState(null);
  const [currentScreen, setCurrentScreen] = useState("landing");
  const [authReady, setAuthReady] = useState(false);
  const [pendingAuthNav, setPendingAuthNav] = useState(false);
  const [authError, setAuthError] = useState("");

  const resolvePostAuthScreen = useCallback(async (uid) => {
    const p = await getUserProfile(uid);
    if (needsOnboarding(p)) {
      setCurrentScreen("onboarding");
    } else {
      setCurrentScreen("app");
    }
  }, []);

  // Handle Spotify OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (!code) return;

    window.history.replaceState({}, document.title, "/");

    exchangeCodeForToken(code).then(async (token) => {
      if (!token) return;

      // "Continue with Spotify" from the sign-in screen: no Tapped account yet,
      // so create one from the Spotify profile before linking.
      if (sessionStorage.getItem("spotify_oauth_source") === "auth") {
        try {
          await auth.authStateReady();
          if (!auth.currentUser) {
            const spotifyProfile = await getSpotifyUserProfile(token);
            if (spotifyProfile.error) {
              throw new Error(spotifyProfile.error.message || "Could not load your Spotify profile");
            }
            await signInWithSpotifyProfile(spotifyProfile);
          }
          localStorage.setItem("tapped_visited", "true");
        } catch (err) {
          console.error("❌ Spotify sign-in failed:", err);
          clearSpotifyToken();
          sessionStorage.removeItem("spotify_oauth_source");
          setAuthError("Could not sign in with Spotify. Please try again or use another method.");
          setCurrentScreen("auth");
          return;
        }
      }

      sessionStorage.setItem("spotify_oauth_pending", "true");
      setSpotifyToken(token);
    });
  }, []);


  // Process pending Spotify OAuth once user is available
  useEffect(() => {
    if (loading || !user || user.isGuest) return;
    if (sessionStorage.getItem("spotify_oauth_pending") !== "true") return;

    const source = sessionStorage.getItem("spotify_oauth_source") || "venue";
    sessionStorage.removeItem("spotify_oauth_pending");
    sessionStorage.removeItem("spotify_oauth_source");

    (async () => {
      const token = getSavedSpotifyToken();
      if (token) {
        await syncSpotifyAccount(user.uid, token);
        await refreshProfile();
      }

      if (source === "onboarding") {
        await updateUserProfile(user.uid, { onboardingComplete: true });
        await refreshProfile();
        setCurrentScreen("app");
      } else if (source === "profile") {
        setCurrentScreen("app");
      } else if (source === "auth") {
        const p = await getUserProfile(user.uid);
        setCurrentScreen(needsOnboarding(p) ? "onboarding" : "app");
      } else {
        localStorage.setItem("tapped_mode", "venue");
        const p = await getUserProfile(user.uid);
        if (needsOnboarding(p)) {
          setCurrentScreen("onboarding");
        } else {
          setCurrentScreen("app");
        }
      }
    })();
  }, [loading, user, refreshProfile, spotifyToken]);

  // Restore saved Spotify token on app load
  useEffect(() => {
    if (loading || !user || user.isGuest) return;

    (async () => {
      let token = getSavedSpotifyToken();
      if (!token) {
        token = await refreshSpotifyToken();
      }
      if (token) {
        setSpotifyToken(token);
        const p = await getUserProfile(user.uid);
        if (!p?.connectedAccounts?.spotify?.connected) {
          await syncSpotifyAccount(user.uid, token);
          await refreshProfile();
        }
      }
    })();
  }, [loading, user, refreshProfile]);

  // Once auth is ready, check if user is already logged in
  useEffect(() => {
    if (!loading && !authReady) {
      setAuthReady(true);
      const hasVisited = localStorage.getItem("tapped_visited");
      if (user && hasVisited && !user.isGuest) {
        resolvePostAuthScreen(user.uid);
      }
    }
  }, [loading, authReady, user, resolvePostAuthScreen]);

  // Navigate after auth screen success
  useEffect(() => {
    if (!pendingAuthNav || loading || !user) return;
    setPendingAuthNav(false);
    if (user.isGuest) {
      setCurrentScreen("app");
      return;
    }
    resolvePostAuthScreen(user.uid);
  }, [pendingAuthNav, loading, user, resolvePostAuthScreen]);

  if (loading && !authReady) {
    return (
      <div style={{
        background: "#0A0A0A", minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'DM Sans', sans-serif"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 48, color: "#fff", letterSpacing: -2, marginBottom: 16
          }}>tapped</div>
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (currentScreen === "landing") {
    return (
      <LandingScreen
        onGetStarted={() => {
          localStorage.setItem("tapped_visited", "true");
          setCurrentScreen("auth");
        }}
      />
    );
  }

  if (currentScreen === "auth") {
    return (
      <AuthScreen
        initialError={authError}
        onSuccess={() => {
          setAuthError("");
          localStorage.setItem("tapped_visited", "true");
          setPendingAuthNav(true);
        }}
        onBack={() => setCurrentScreen("landing")}
      />
    );
  }

  if (currentScreen === "onboarding") {
    return (
      <OnboardingScreen
        onComplete={() => setCurrentScreen("app")}
      />
    );
  }

  return (
    <VenueProvider>
      <MainApp
        spotifyToken={spotifyToken}
        setSpotifyToken={setSpotifyToken}
        onLogout={() => {
          localStorage.removeItem("tapped_visited");
          setCurrentScreen("landing");
        }}
      />
    </VenueProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRouter />
    </AuthProvider>
  );
}
